// pages/AddProductVariant.js
import React, { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import Swal from "sweetalert2";
import { useDropzone } from "react-dropzone";
import { FaTrash } from "react-icons/fa";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import ProductNavbar from "./ProductNavbar";

function VariantCard({ index, variant, onChange, onRemove, openParamModal }) {
  const handleFieldChange = (field, value) => {
    onChange(variant.variant_id || index, field, value); // use variant_id if exists, else index
  };

  const handleRemoveImage = (idx) => {
    const newImages = variant.images.filter((_, i) => i !== idx);
    onChange(variant.variant_id || index, "images", newImages);
  };

  const onDrop = useCallback(
    (files) => {
      const selectedFiles = Array.from(files).slice(0, 10 - variant.images.length);
      onChange(
        variant.variant_id || index,
        "images",
        [
          ...variant.images,
          ...selectedFiles.map((f) => Object.assign(f, { preview: URL.createObjectURL(f), file: true })),
        ]
      );
    },
    [variant, onChange, index]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: true,
    maxFiles: 10 - variant.images.length,
  });

  return (
    <div className="card p-4 shadow-sm w-100 position-relative">
      <h5>Variant {index + 1}</h5>
      <button
        type="button"
        className="btn btn-danger btn-sm position-absolute"
        style={{ top: "10px", right: "10px" }}
        onClick={() => onRemove(variant.variant_id || index)}
      >
        &times;
      </button>

      {/* Variant Name */}
      <div className="mb-3">
        <label className="form-label">Variant Name</label>
        <input
          type="text"
          className="form-control"
          value={variant.name}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          placeholder="Variant Name"
          required
        />
      </div>

      {/* Quantity & Unit */}
      <div className="mb-3 row">
        <div className="col-md-6 mb-2">
          <label className="form-label">Quantity</label>
          <input
            type="number"
            className="form-control"
            value={variant.quantity}
            onChange={(e) => handleFieldChange("quantity", e.target.value)}
            placeholder="Quantity"
            min={1}
            required
          />
        </div>
        <div className="col-md-6 mb-2">
          <label className="form-label">Unit</label>
          <input
            type="text"
            className="form-control"
            value={variant.unit}
            onChange={(e) => handleFieldChange("unit", e.target.value)}
            placeholder="pcs, g, kg, ml, l..."
            required
          />
        </div>
      </div>

      {/* Price, MRP, Stock, SKU */}
      <div className="mb-3 row">
        <div className="col-md-3 mb-2">
          <label className="form-label">Price</label>
          <input
            type="number"
            className="form-control"
            value={variant.price}
            onChange={(e) => handleFieldChange("price", e.target.value)}
            placeholder="Price"
            required
          />
        </div>
        <div className="col-md-3 mb-2">
          <label className="form-label">MRP</label>
          <input
            type="number"
            className="form-control"
            value={variant.mrp}
            onChange={(e) => handleFieldChange("mrp", e.target.value)}
            placeholder="MRP"
            required
          />
        </div>
        <div className="col-md-3 mb-2">
          <label className="form-label">Stock</label>
          <input
            type="number"
            className="form-control"
            value={variant.stock}
            onChange={(e) => handleFieldChange("stock", e.target.value)}
            placeholder="Stock"
            required
          />
        </div>
        <div className="col-md-3 mb-2">
          <label className="form-label">SKU</label>
          <input
            type="text"
            className="form-control"
            value={variant.sku}
            onChange={(e) => handleFieldChange("sku", e.target.value)}
            placeholder="SKU"
          />
        </div>
      </div>

      {/* Optional Color */}
      <div className="mb-3">
        <div className="form-check form-switch mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            checked={variant.useColor || false}
            onChange={(e) => handleFieldChange("useColor", e.target.checked)}
            id={`useColorSwitch${variant.variant_id || index}`}
          />
          <label className="form-check-label" htmlFor={`useColorSwitch${variant.variant_id || index}`}>
            Enable Color
          </label>
        </div>
        {variant.useColor && (
          <div className="d-flex gap-2 align-items-center">
            <input
              type="color"
              className="form-control form-control-color"
              value={variant.colorCode || "#ffffff"}
              onChange={(e) => handleFieldChange("colorCode", e.target.value)}
            />
            <input
              type="text"
              className="form-control"
              placeholder="Color Name"
              value={variant.colorName || ""}
              onChange={(e) => handleFieldChange("colorName", e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Custom Parameters */}
      <div className="mb-3">
        <label className="form-label">Parameters</label>
        {Object.keys(variant.parameters).map((key) => (
          <div className="d-flex gap-2 mb-2" key={key}>
            <input
              type="text"
              className="form-control"
              value={variant.parameters[key]}
              onChange={(e) =>
                onChange(variant.variant_id || index, "parameters", { ...variant.parameters, [key]: e.target.value })
              }
              placeholder={key}
            />
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => {
                const newParams = { ...variant.parameters };
                delete newParams[key];
                onChange(variant.variant_id || index, "parameters", newParams);
              }}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => openParamModal(variant.variant_id || index)}
        >
          + Add Parameter
        </button>
      </div>

      {/* Image Gallery */}
      <div className="mb-3">
        <label className="form-label">Variant Images (Max 10)</label>
        <div
          {...getRootProps()}
          className="border border-dashed p-3"
          style={{ minHeight: "80px", cursor: "pointer", borderRadius: "6px" }}
        >
          <input {...getInputProps()} />
          {variant.images.length === 0 ? (
            <p style={{ fontSize: "12px", textAlign: "center" }}>
              {isDragActive ? "Drop here..." : "Drag & drop images or click"}
            </p>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {variant.images.map((file, idx) => (
                <div key={idx} style={{ position: "relative", width: "80px", height: "80px" }}>
                  <img
                    src={`${imgurl}${file.path}`}
                    alt="preview"
                    style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px" }}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(idx);
                    }}
                    style={{
                      position: "absolute",
                      top: "-6px",
                      right: "-6px",
                      background: "#ff4d4f",
                      border: "none",
                      borderRadius: "50%",
                      width: "22px",
                      height: "22px",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductVariant() {
  const { uuid } = useParams();
  const [variants, setVariants] = useState([]);
  const [showParamModal, setShowParamModal] = useState(false);
  const [currentVariantId, setCurrentVariantId] = useState(null);
  const [newParamKey, setNewParamKey] = useState("");
  const [newParamValue, setNewParamValue] = useState("");
  const [loading, setLoading] = useState(false);

// Fetch variants
useEffect(() => {
  const fetchVariants = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseurl}getProductVariants/${uuid}`);
      if (res.data.variants && res.data.variants.length > 0) {
        const mapped = res.data.variants.map((v) => ({
          variant_id: v.id || v.variant_id, // keep existing variant_id from API
          ...v.variant_json,
          images: v.variant_json.images || [],
        }));
        setVariants(mapped);
      } else {
        setVariants([
          {
            name: "",
            quantity: 1,
            unit: "pcs",
            price: "",
            mrp: "",
            stock: "",
            sku: "",
            parameters: {},
            images: [],
            useColor: false,
            colorName: "",
            colorCode: "#ffffff",
          },
        ]);
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch variants", "error");
    } finally {
      setLoading(false);
    }
  };
  fetchVariants();
}, [uuid]);

  const handleVariantChange = (idOrIndex, field, value) => {
    setVariants((prev) =>
      prev.map((v, idx) =>
        (v.variant_id ? v.variant_id === idOrIndex : idx === idOrIndex) ? { ...v, [field]: value } : v
      )
    );
  };

  const addVariant = () => {
    setVariants((prev) => [
      ...prev,
      {
        name: "",
        quantity: 1,
        unit: "pcs",
        price: "",
        mrp: "",
        stock: "",
        sku: "",
        parameters: {},
        images: [],
        useColor: false,
        colorName: "",
        colorCode: "#ffffff",
      },
    ]);
  };

  const removeVariant = async (idOrIndex) => {
  const variantToRemove = variants.find((v, idx) =>
    v.variant_id ? v.variant_id === idOrIndex : idx === idOrIndex
  );

  // If variant has a variant_id, call API to delete it
  if (variantToRemove && variantToRemove.variant_id) {
    try {
      const res = await axios.delete(
        `${baseurl}deleteProductVariant/${uuid}/${variantToRemove.variant_id}`
      );

      if (res.data.message) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: res.data.message,
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });

        // Remove from local state
        setVariants((prev) =>
          prev.filter((v) =>
            v.variant_id ? v.variant_id !== idOrIndex : true
          )
        );
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to delete variant",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
    }
  } else {
    // If variant is not saved yet, just remove from local state
    setVariants((prev) =>
      prev.filter((v, idx) => (v.variant_id ? true : idx !== idOrIndex))
    );
  }
};


  const openParamModal = (idOrIndex) => {
    setCurrentVariantId(idOrIndex);
    setNewParamKey("");
    setNewParamValue("");
    setShowParamModal(true);
  };

  const addParameterFromModal = () => {
    if (!newParamKey) return;
    handleVariantChange(currentVariantId, "parameters", {
      ...variants.find((v, idx) =>
        v.variant_id ? v.variant_id === currentVariantId : idx === currentVariantId
      ).parameters,
      [newParamKey]: newParamValue,
    });
    setShowParamModal(false);
  };

// Submit handler
const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    setLoading(true);

    const formData = new FormData();
    formData.append("variants", JSON.stringify(variants));

    // Append files
    variants.forEach((v) => {
      v.images.forEach((img) => {
        if (img.file === true && img instanceof File) {
          formData.append("variant_images", img);
        }
      });
    });

    // Debug log all FormData entries
    console.log("Submitting FormData:");
    for (let pair of formData.entries()) {
      console.log(pair[0], pair[1]);
    }

    const res = await axios.post(`${baseurl}addUpdateProductVariants/${uuid}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    // SweetAlert2 Toast
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      },
    });

    if (res.data.variants) {
      Toast.fire({
        icon: "success",
        title: "Variants saved successfully!",
      });

      // Update local state with backend variant_ids
      const updatedVariants = res.data.variants.map((v) => ({
        variant_id: v.id || v.variant_id, // backend ID
        ...v.variant_json,
        images: v.variant_json.images || [],
      }));
      setVariants(updatedVariants);
    }
  } catch (err) {
    console.error(err);

    // Error toast
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });

    Toast.fire({
      icon: "error",
      title: "Failed to save variants",
    });
  } finally {
    setLoading(false);
  }
};


  if (loading) return <p className="text-center mt-4">Loading...</p>;

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="main-content">


        <ProductNavbar/>

          <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
            <div className="breadcrumb-title pe-3">Manage Variants</div>
          </div>

          <div className="col-12 mb-4">
            <form onSubmit={handleSubmit} className="d-flex flex-column gap-4">
              {variants.map((v, index) => (
                <VariantCard
                  key={v.variant_id || index}
                  index={index}
                  variant={v}
                  onChange={handleVariantChange}
                  onRemove={removeVariant}
                  openParamModal={openParamModal}
                />
              ))}

              <div className="d-flex gap-2">
                <button type="button" className="btn btn-success" onClick={addVariant}>
                  + Add Variant
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Variants
                </button>
              </div>
            </form>
          </div>

          {/* Parameter Modal */}
          {showParamModal && (
            <div className="modal show d-block" tabIndex="-1" style={{ background: "#0000007d" }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Add Parameter</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowParamModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Parameter Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newParamKey}
                        onChange={(e) => setNewParamKey(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Parameter Value</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newParamValue}
                        onChange={(e) => setNewParamValue(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowParamModal(false)}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={addParameterFromModal}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

export default ProductVariant;
